import { CartAddEvent } from '@theme/events';
import { morphSection } from '@theme/section-renderer';

if (!window.FastCart) {
  const cartRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const pendingButtons = new WeakSet();
  const COMPONENT_WAIT_MS = 1500;

  function setButtonState(button, textEl, state, originalText) {
    switch (state) {
      case 'loading':
        button.disabled = true;
        button.classList.add('is-adding');
        button.classList.remove('is-added', 'is-error');
        if (textEl) textEl.textContent = 'Adding...';
        break;
      case 'added':
        button.classList.remove('is-adding');
        button.classList.add('is-added');
        if (textEl) textEl.textContent = 'Added';
        break;
      case 'error':
        button.classList.remove('is-adding');
        button.classList.add('is-error');
        if (textEl) textEl.textContent = 'Error';
        break;
      case 'reset':
        button.classList.remove('is-adding', 'is-added', 'is-error');
        if (textEl) textEl.textContent = originalText;
        button.disabled = false;
        break;
    }
  }

  function normalizeSectionId(sectionId) {
    return String(sectionId || '').replace(/^shopify-section-/, '');
  }

  function getCartSectionIds() {
    const ids = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach(function (el) {
      const sectionId = normalizeSectionId(el.dataset.sectionId);
      if (sectionId && !ids.includes(sectionId)) ids.push(sectionId);
    });
    return ids;
  }

  function isEmptyCartSectionHtml(html) {
    return (
      html.includes('cart-drawer__heading--empty') || html.includes('cart-items__empty-button')
    );
  }

  function isValidCartSectionHtml(html, cart) {
    if (!html) return false;
    if (!cart.item_count) return true;
    return !isEmptyCartSectionHtml(html);
  }

  function isDrawerEmpty() {
    return (
      document.querySelector('.cart-drawer__heading--empty') !== null ||
      document.querySelector('.cart-items__empty-button') !== null
    );
  }

  function whenDefinedWithTimeout(name, timeoutMs) {
    if (customElements.get(name)) return Promise.resolve();
    return Promise.race([
      customElements.whenDefined(name),
      new Promise(function (resolve) {
        setTimeout(resolve, timeoutMs);
      }),
    ]);
  }

  async function fetchSectionHtml(sectionIds) {
    const url = new URL(window.location.href);
    url.search = '';
    url.searchParams.set('sections', sectionIds.join(','));
    url.searchParams.set('sections_url', window.location.pathname);
    url.searchParams.set('_fd', String(Date.now()));

    const response = await fetch(url.toString());
    if (!response.ok) throw new Error('Section refresh failed: ' + response.status);
    return response.json();
  }

  async function ensureValidCartSections(sectionIds, sections, cart) {
    let result = Object.assign({}, sections || {});

    if (!sectionIds.length || !cart.item_count) return result;

    if (sectionIds.every(function (id) { return isValidCartSectionHtml(result[id], cart); })) {
      return result;
    }

    for (let attempt = 0; attempt < 3; attempt++) {
      if (attempt > 0) {
        await new Promise(function (resolve) {
          setTimeout(resolve, 150 * attempt);
        });
      }

      const fetched = await fetchSectionHtml(sectionIds);
      result = Object.assign(result, fetched);

      if (sectionIds.every(function (id) { return isValidCartSectionHtml(result[id], cart); })) {
        break;
      }
    }

    return result;
  }

  function replaceDrawerInnerFromSectionHtml(sectionId, sectionHtml) {
    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const sectionEl = doc.getElementById('shopify-section-' + sectionId);
    const newInner = sectionEl && sectionEl.querySelector('[data-hydration-key="cart-drawer-inner"]');
    const oldInner = document.querySelector('[data-hydration-key="cart-drawer-inner"]');

    if (!(newInner instanceof Element) || !(oldInner instanceof Element)) return false;

    oldInner.replaceChildren(...Array.from(newInner.childNodes));
    return true;
  }

  async function applyCartSectionUpdates(sectionIds, sections) {
    await whenDefinedWithTimeout('cart-items-component', COMPONENT_WAIT_MS);

    let anyApplied = false;

    for (let i = 0; i < sectionIds.length; i++) {
      const sectionId = sectionIds[i];
      const html = sections[sectionId];
      if (!html) continue;

      try {
        const el = document.querySelector('cart-items-component[data-section-id="' + sectionId + '"]');

        if (el && typeof el.applySectionFromHtml === 'function') {
          await el.applySectionFromHtml(html);
          anyApplied = true;
          continue;
        }

        if (isDrawerEmpty() && replaceDrawerInnerFromSectionHtml(sectionId, html)) {
          anyApplied = true;
          continue;
        }

        await morphSection(sectionId, html, { mode: 'hydration' });
        anyApplied = true;
      } catch (error) {
        console.error('Cart section update failed:', error);
      }
    }

    return anyApplied;
  }

  function updateCartIconBubble(itemCount) {
    document.querySelectorAll('cart-icon').forEach(function (cartIcon) {
      const countEl = cartIcon.querySelector('.cart-bubble__text-count');
      const bubbleEl = cartIcon.querySelector('.cart-bubble');

      if (countEl) {
        countEl.textContent = itemCount > 0 && itemCount < 100 ? String(itemCount) : '';
        countEl.classList.toggle('hidden', itemCount === 0);
      }

      if (bubbleEl) {
        bubbleEl.classList.toggle('visually-hidden', itemCount === 0);
      }

      cartIcon.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);
    });
  }

  function dispatchCartUpdate(cart, variantId, sections, sectionsApplied) {
    updateCartIconBubble(cart.item_count);

    document.dispatchEvent(
      new CartAddEvent(cart, String(variantId), {
        source: 'fast-cart',
        itemCount: cart.item_count,
        variantId: String(variantId),
        sections: sections || {},
        sectionsApplied: sectionsApplied === true,
      })
    );
  }

  window.FastCart = {
    cartIconSectionId: null,

    add: function (variantId, button, opts) {
      opts = opts || {};
      if (!variantId || !button || pendingButtons.has(button)) return;
      pendingButtons.add(button);

      const textEl = button.querySelector('[data-button-text]') || button;
      const originalText = textEl.textContent.trim();
      const sectionIds = getCartSectionIds();

      requestAnimationFrame(function () {
        setButtonState(button, textEl, 'loading', originalText);
      });

      const payload = {
        items: [{ id: Number(variantId), quantity: opts.quantity || 1 }],
      };

      if (sectionIds.length) {
        payload.sections = sectionIds.join(',');
        payload.sections_url = window.location.pathname;
      }

      fetch(cartRoot + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      })
        .then(function (response) {
          return response.json().then(function (body) {
            if (!response.ok || body.status) {
              return Promise.reject(body);
            }
            return body;
          });
        })
        .then(function (addResult) {
          return fetch(cartRoot + 'cart.js', {
            headers: { Accept: 'application/json' },
          })
            .then(function (response) {
              return response.json();
            })
            .then(function (cart) {
              return {
                cart: cart,
                sections: addResult.sections || {},
              };
            });
        })
        .then(async function (data) {
          if (!data.cart || !data.cart.item_count) {
            throw new Error('Cart did not update');
          }

          // Update button, count, and open drawer immediately — never block on section HTML.
          setButtonState(button, textEl, 'added', originalText);
          dispatchCartUpdate(data.cart, variantId, data.sections || {}, false);

          if (sectionIds.length) {
            try {
              const sections = await ensureValidCartSections(
                sectionIds,
                data.sections || {},
                data.cart
              );
              await applyCartSectionUpdates(sectionIds, sections);

              if (isDrawerEmpty()) {
                await applyCartSectionUpdates(sectionIds, sections);
              }
            } catch (error) {
              console.error('Cart section refresh failed:', error);
            }
          }
        })
        .then(function () {
          setTimeout(function () {
            setButtonState(button, textEl, 'reset', originalText);
            pendingButtons.delete(button);
          }, 400);
        })
        .catch(function (error) {
          console.error('Add to cart error:', error);
          setButtonState(button, textEl, 'error', originalText);
          setTimeout(function () {
            setButtonState(button, textEl, 'reset', originalText);
            pendingButtons.delete(button);
          }, 1000);
        });
    },
  };
}
